<template>
  <div :class="['navbar--fixed', isRestaurant && 'is-restaurant', isRestaurantDetail && 'is-restaurant']">
    <div class="container--navbar navbar">
      <NavbarSearchInput
        v-if="searchBar.active"
        @search-text-change="searchGlobal"
        @close="searchBarActiveHasChange"
      />

      <mq-layout
        :mq="['lg', 'xl']"
        :class="[
          searchBar.active
            ? 'container--navbar__hidden'
            : 'container--navbar__hidden container--navbar__showing',
        ]"
      >
        <NavbarDesktop
          v-if="!searchBar.active"
          :navbar="navbar"
          :selected-action-values="getSelectedActionValues"
          :locked-action-bar-types="lockedActionBarTypes"
          :css-style="cssStyle"
          @action-bar-value-has-change="actionBarValueHasChange"
          @search-bar-active-has-change="searchBarActiveHasChange"
        />
      </mq-layout>

      <mq-layout :mq="['sm', 'md']">
        <NavbarMobile
          v-if="!searchBar.active"
          :navbar="navbar"
          :selected-action-values="getSelectedActionValues"
          :locked-action-bar-types="lockedActionBarTypes"
          :css-style="cssStyle"
          @action-bar-value-has-change="actionBarValueHasChange"
          @search-bar-active-has-change="searchBarActiveHasChange"
        />
      </mq-layout>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from "vuex";
import { isEmpty, map, startsWith, keyBy, head } from "@/utils/lodash";
import cssVariables from "@/assets/variables.scss";
import logo from "@/assets/images/logo/logo-white.png";
import NavbarDesktop from "@/components/navbar/navbar-desktop.vue";
import NavbarMobile from "@/components/navbar/navbar-mobile.vue";
import NavbarSearchInput from "@/components/navbar/navbar-search-input.vue";
import Store from '@/store';
import userApi from "@/api/user";


const actionBarTypeCodes = {
  LANGUAGE: "LANGUAGE",
  COUNTRY: "COUNTRY",
  TAX: "TAX",
};

export default {
  components: {
    NavbarDesktop,
    NavbarMobile,
    NavbarSearchInput,
  },
  data() {
    return {
      selectedLanguageCode: "FR",
      countryCode: '',
      searchBar: {
        active: false,
      },
      cssStyle: {
        backgroundColor: "transparent",
        textColor: cssVariables.colorOnPrimary,
        activeTextColor: cssVariables.colorOnPrimary,
        subMenuGroupColors: [
          `${cssVariables.colorPrimary}`,
          // TODO: Map this color as a variable into @/assets/variables.scss
          // `${cssVariables.colorPrimary}`,
        ],
      },
      previousPath: this.$route.path,
    };
  },
  computed: {
    ...mapState("configuration", ["actionBar"]),
    ...mapState("user", ["user"]),
    ...mapGetters("user", ["isLoggedIn"]),

    navbar() {
      return {
        logo: {
          path: "/",
          src: logo,
        },
        menus: this.getFormattedMenus(),
        actionBar: this.getFormattedActionBar(),
      };
    },
    getSelectedActionValues() {
      return {
        language: this.selectedLanguageCode,
        country: this.actionBar.country,
        tax: this.actionBar.tax,
      };
    },
    isRestaurant(){
      return this.$route && this.$route.fullPath.indexOf('inventory=') > -1 ? true : false;
    },
    isRestaurantDetail() {
      return this.$route && this.$route.fullPath.indexOf('productType=') > -1  ? true : false;
    },
    // Market and tax are the two action-bar values the checkout is priced
    // against: the cart is per-market (cartProducts<QC|ON> in localStorage),
    // the calculation comes back from getCartCalculation() for that market,
    // and checkout.vue snapshots the market once in data() and never listens
    // for `chancountryCode`. Switching either mid-order therefore left the
    // customer paying against one market's calculation while the rest of the
    // app had already moved to the other. Language is unaffected — it changes
    // no price — so it stays available.
    lockedActionBarTypes() {
      return this.$route && this.$route.name === "checkout"
        ? [actionBarTypeCodes.COUNTRY, actionBarTypeCodes.TAX]
        : [];
    },
  },
  mounted() {
    const countryVal = localStorage.getItem('countryCode');
    if (!countryVal) {
      localStorage.setItem("countryCode", "QC");
    }
    const taxVal = localStorage.getItem('tax');
    if (!taxVal) {
      localStorage.setItem("tax", "IN");
    }

    this.initializeCountry();
    if (
      localStorage.getItem("setLangRequestValue") !== null &&
      localStorage.getItem("setLangCode") !== null
    ) {
      var code = localStorage.getItem("setLangCode");
      var requestValue = localStorage.getItem("setLangRequestValue");
      this.setDefaultLang(code, requestValue);
    }
      this.$root.$on("changeProductPage", (lang) => {
        
        let country = keyBy(
          head(this.$i18n.t("common.navbar.actionBar.country.subMenuGroups"))
            .items,
          "code"
        )["QC"];
        if (lang.code == "EN") {
          country = keyBy(
            head(this.$i18n.t("common.navbar.actionBar.country.subMenuGroups"))
              .items,
            "code"
          )["ON"];
        }
        this.updateCountryActions(country);
      });
  },
  watch:{
    $route() {
      this.initializeCountry();
    },
  },
  methods: {
    ...mapActions("configuration", ["setActionBarCountry", "setActionBarTax"]),
    ...mapActions("user", ["logout"]),
    ...mapActions("shoppingCart", ["updateShoppingCart", "fetchShoppingCart"]),
    ...mapActions("product", ["fetchProductsByPuid"]),

    initializeCountry() {
      const countryPath = this.$route.path || "/";
      this.countryCode = countryPath.slice(1).toUpperCase();

      if (this.countryCode) {
        localStorage.setItem("countryCode", this.actionBar.country);
      }
        let country = keyBy(
        head(this.$i18n.t("common.navbar.actionBar.country.subMenuGroups"))
          .items,
        "code"
      )[this.countryCode];
        
        this.updateCountryActions(country);
      // }
    },
    formatKeyValueQueryString(parameters) {
      return map(
        parameters,
        (parameter) => `${parameter.key}=${parameter.value}`
      ).join("&");
    },
    getFormattedMenus() {
      const navbarRouteIndexedByView = {
        BOUTIQUE: {
          path: "/products?",
        },
        PRODUCTS: {
          path: "/products",
        },
        PROVIDERS: {
          path: "/providers",
          parameters: [
            {
              key: "type",
              value: this.actionBar.country,
            },
          ],
        },
        CLIENTS: {
          path: "/account",
        },
        BLOG: {
          path: "/blogs",
        },
        ABONNEMENT: {},
      };

      return map(this.$i18n.t("common.navbar.menus"), (menu) => {
        let path;
        let subMenuGroups;

        if (!isEmpty(navbarRouteIndexedByView[menu.code].parameters)) {
          path = `${
            navbarRouteIndexedByView[menu.code].path
          }?${this.formatKeyValueQueryString(
            navbarRouteIndexedByView[menu.code].parameters
          )}`;
        } else {
          path = navbarRouteIndexedByView[menu.code].path;
        }

        if (!isEmpty(menu.subMenuGroups)) {
          if (menu.subMenuGroups.length > 4) {
            throw new Error(
              "@/components/navbar/navbar.vue : A sub menu groups cannot be bigger than 4 to prevent a division by 5 (24 / 5)"
            );
          }

          subMenuGroups = map(menu.subMenuGroups, (subMenuGroup) => ({
            ...subMenuGroup,
            items: map(subMenuGroup.items, (item) => ({
              ...item,
              path:
                item.type == "single"
                  ? `${item.path}`
                  : `${
                      navbarRouteIndexedByView[menu.code].path
                    }?${this.formatKeyValueQueryString(item.inventories)}`,
            })),
          }));
        }

        return {
          ...menu,
          path,
          subMenuGroups,
        };
      });
    },
    getFormattedActionBar() {
      return [
        {
          type: actionBarTypeCodes.LANGUAGE,
          model: this.selectedLanguageCode,
          ...this.$i18n.t("common.navbar.actionBar.language"),
        },
        {
          type: actionBarTypeCodes.COUNTRY,
          model: this.actionBar.country,
          ...this.$i18n.t("common.navbar.actionBar.country"),
        },
        {
          type: actionBarTypeCodes.TAX,
          model: this.actionBar.tax,
          ...this.$i18n.t("common.navbar.actionBar.tax"),
        },
      ];
    },
    updateLanguageActions(language) {
      localStorage.setItem("setLangCode", language.code);
      localStorage.setItem("setLangRequestValue", language.requestValue);
      this.selectedLanguageCode = language.code;
      this.$i18n.locale = language.requestValue;
      this.$root.$emit("chanLang", this.$i18n.locale);
    },
    updateCountryActions(country) {
       // Commit the new market to Vuex BEFORE announcing it. Emitting first left
       // every `chancountryCode` listener reading a stale `actionBar.country`
       // (localStorage was already updated, Vuex was not), so anything resolving
       // a product against `actionBar.country` picked the OLD market's inventory
       // variant. On a producer page that showed the other market's price with a
       // degraded "Réserver" button instead of "Ajouter au panier", and a QC
       // product could come back looking like an SAQ listing.
       if (country && country.code) {
          localStorage.setItem("countryCode", country.code);
       }

       this.setActionBarCountry({ country });

       if (country && country.code) {
          this.$root.$emit("chancountryCode", country.code);
       }
      if (
        startsWith(this.$router.currentRoute.fullPath, "/providers") &&
        this.$router.currentRoute?.query?.type !== country?.code
      ) {
        this.$router.push({
          query: {
            ...this.$route.query,
            type: country?.code,
          },
        });
      }
    },
    updateTaxActions(tax) {
      this.setActionBarTax({ tax });
      this.$root.$emit("changeTax", tax);
      localStorage.setItem("tax", tax.code);
    },
    async actionBarValueHasChange({ value, type }) {
      // Enforced here rather than only in the two navbars: this is the single
      // path every action-bar change goes through, desktop and mobile alike.
      // The controls are also disabled visually, so reaching this is either a
      // control we missed or a programmatic call.
      if (this.lockedActionBarTypes.indexOf(type) > -1) {
        this.$message({
          message: this.$i18n.t("common.navbar.actionBar.lockedDuringCheckout"),
          type: "warning",
        });
        return;
      }

      const getCountryCode = localStorage.getItem('actualCountryCode');
      if (type === actionBarTypeCodes.LANGUAGE) {
        this.updateLanguageActions(value);
      }

      if (type === actionBarTypeCodes.COUNTRY) {
        if(this.isLoggedIn == true) {
          if ((getCountryCode == 'QC' && value.code == 'ON') || (getCountryCode == 'ON' && value.code == 'QC')) {
            this.handleLogout();
          }
        }
        if (this.isLoggedIn == false) {
          if(this.$route.query.inventory && this.$route.query.inventory == 'STORE_TORONTO' && value.code == 'QC'){
            // this.$router.push('products?inventory=STORE_TORONTO');
            this.$router.push('products?inventory=STORE_MTL');
          }
          if(this.$route.query.inventory && this.$route.query.inventory == 'STORE_MTL' && value.code == 'ON'){
            this.$router.push('products?inventory=STORE_TORONTO');
            // this.$router.push('products?inventory=STORE_MTL');
          }
          this.updateCountryActions(value);
          if(getCountryCode == value.code) {
              const accessToken = sessionStorage.getItem("access-token");
              const user = JSON.parse(sessionStorage.getItem("user"));
              const customer = JSON.parse(sessionStorage.getItem("customer"));
              if (user && accessToken) {
                localStorage.setItem('user', JSON.stringify(user));
                localStorage.setItem('customer', customer);
                localStorage.setItem('access-token', accessToken);
                localStorage.setItem('token', accessToken)
              }
              
            await this.updateShoppingCartUser().then(() => {
                  if(this.user && this.user.passwordExpired == true){
                    this.$router.push('/reset-password');
                  }
                  else if (this.$route.query.redirect) {
                    this.$router.push(this.$route.query.redirect);
                  } else {
                    // Same fix as authenticate-form.vue: the guest cart is stored
                    // per market as `cartProducts<countryCode>`. The unsuffixed key
                    // is never written, so this read null and asked the catalogue
                    // for an empty puid list, which came back empty and wiped the
                    // products the cart page renders from.
                    const cartKey = 'cartProducts' + (localStorage.getItem('countryCode') === 'ON' ? 'ON' : 'QC');
                    let getProducts = null;
                    try {
                      getProducts = JSON.parse(localStorage.getItem(cartKey));
                    } catch (e) {
                      getProducts = null;
                    }
                    const puids = map(getProducts, (p) => p.puid).filter(Boolean)
                    if (puids.length) {
                      this.fetchProductsByPuid({
                        puids,
                      })
                    }
                    if(this.$route.path && this.$route.path == '/shopping-cart-empty'){
                      this.$router.push('/shopping-cart');
                    }
                  }
            });
            await userApi.findById(JSON.parse(localStorage.user).user.id).then((user) => {
                  Store.dispatch("user/setUserProfile", user);
            });
            console.log(this.$route.path, this.isLoggedIn);
            if(this.isLoggedIn && this.$route.path == '/login'){
              if(this.previousPath == '/account'){
                this.$router.push("/account")
              }else{
                this.$router.back();
                this.$router.go(-1);
              }
            }
          }
        }
      }

      if (type === actionBarTypeCodes.TAX) {
        this.updateTaxActions(value);
      }
    },
    updateShoppingCartUser() {
      const vm = this;
      const getId = JSON.parse(sessionStorage.getItem("user"));
      const user = {
        id: getId?.id,
      };
      if (getId) {
        return Promise.resolve(
          this.fetchShoppingCart({
            id: getId.id,
          })
            .then(() => {
              const shoppingCart = {
                lockVersion: vm.shoppingCart.lockVersion,
                user,
                products: vm.shoppingCart.products,
              };
              const cartId = user.id
              vm.updateShoppingCart({
                id: cartId,
                shoppingCart,
              });
            })
            .catch((error) => {
              console.log(error);
            })
        );
      }
      return Promise.resolve();
    },
    searchGlobal({ text }) {
      this.$router
        .push({
          path: "/global-search",
          query: {
            t: text,
          },
        })
        .catch(() => {});
      this.searchBarActiveHasChange();
    },
    searchBarActiveHasChange() {
      this.searchBar.active = !this.searchBar.active;
    },
    setDefaultLang(code, requestValue) {
      this.selectedLanguageCode = code;
      this.$i18n.locale = requestValue;
    },
    handleLogout() {
      Store.dispatch('user/logout');
      Store.dispatch('shoppingCart/emptyShoppingCart');
      Store.dispatch('user/createTempUser');
      this.logout();
      this.previousPath = this.$route.path;
      if(this.$route.path && this.$route.query.inventory == '/products'){
        this.$router.push('products');
      }else if(this.$route.path && (this.$route.path == '/shopping-cart' || this.$route.path == '/account')){
        this.$router.push("/login")
      }
      // if(this.$route.path && this.$route.query.inventory == '/products' && value.code == 'ON'){
      //   this.$router.push('products?inventory=STORE_TORONTO');
      // }
      // this.$router.push("/login");
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/variables.scss";
.navbar--fixed {
  background-color: $color-primary;
  position: fixed;
  top: 0;
  z-index: 9999;
  width: 100%;
  border-bottom: solid 2px rgba(255, 255, 255, 0.2) !important;
}
.navbar {
  height: 50px;
  // background-color: $color-primary;
  width: 100%;
}
.container--navbar {
  min-width: $container-min-width;

  // sm -
  @media screen and (max-width: $media-md) {
    max-width: $container-min-width;
  }
  // md
  @media screen and (min-width: $media-md) and (max-width: $media-lg) {
    max-width: $container-max-width-md;
  }
  // lg +
  @media screen and (min-width: $media-lg) {
    max-width: $media-lg;
  }

  margin-left: auto;
  margin-right: auto;
}
.container--navbar__hidden {
  transform: translateX(-100vw);
  transition: all 0.3s;
}
.container--navbar__showing {
  transform: translateX(0);
}
</style>
